import { useEffect, useRef, useCallback, useState } from "react"

// Constants for idle timeout
export const IDLE_TIMEOUT = 15 * 60 * 1000  // 15 minutes in milliseconds
export const WARNING_BEFORE = 2 * 60 * 1000 // Show warning 2 minutes before timeout

// localStorage key for cross-tab synchronization
const LAST_ACTIVITY_KEY = "lastActivityTime"

/**
 * Custom hook to track user inactivity and manage idle timeout
 * 
 * @param {Object} options
 * @param {Function} options.onTimeout - Callback when idle timeout is reached
 * @param {Function} options.onWarning - Callback when warning time is reached (2 min before timeout)
 * @param {boolean} options.enabled - Whether idle tracking is enabled (default: true)
 * @returns {Object} { resetTimer, isWarning, remainingTime }
 */
const useIdleTimeout = ({ onTimeout, onWarning, enabled = true }) => {
  const timeoutRef = useRef(null)
  const warningTimeoutRef = useRef(null)
  const intervalRef = useRef(null)
  const [isWarning, setIsWarning] = useState(false)
  const [remainingTime, setRemainingTime] = useState(IDLE_TIMEOUT)

  // Events to track for user activity
  const events = ["click", "keypress", "scroll", "mousemove", "touchstart"]

  // Update last activity time in localStorage for cross-tab sync
  const updateLastActivity = useCallback(() => {
    const now = Date.now()
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
  }, [])

  // Get last activity time from localStorage
  const getLastActivity = useCallback(() => {
    const stored = localStorage.getItem(LAST_ACTIVITY_KEY)
    return stored ? parseInt(stored, 10) : Date.now()
  }, [])

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
      warningTimeoutRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Handle timeout - trigger logout
  const handleTimeout = useCallback(() => {
    clearTimers()
    setIsWarning(false)
    setRemainingTime(0)
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    if (onTimeout) {
      onTimeout()
    }
  }, [clearTimers, onTimeout])

  // Handle warning - show warning modal
  const handleWarning = useCallback(() => {
    setIsWarning(true)
    setRemainingTime(WARNING_BEFORE)
    
    // Start countdown interval
    intervalRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        const newTime = prev - 1000
        if (newTime <= 0) {
          clearInterval(intervalRef.current)
          return 0
        }
        return newTime
      })
    }, 1000)

    if (onWarning) {
      onWarning()
    }
  }, [onWarning])

  // Start or reset the idle timer
  const resetTimer = useCallback(() => {
    clearTimers()
    setIsWarning(false)
    setRemainingTime(IDLE_TIMEOUT)
    updateLastActivity()

    if (!enabled) return

    // Set warning timeout (IDLE_TIMEOUT - WARNING_BEFORE)
    warningTimeoutRef.current = setTimeout(() => {
      handleWarning()
    }, IDLE_TIMEOUT - WARNING_BEFORE)

    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      handleTimeout()
    }, IDLE_TIMEOUT)
  }, [enabled, clearTimers, updateLastActivity, handleWarning, handleTimeout])

  // Debounced activity handler to avoid excessive calls
  const lastActivityCall = useRef(0)
  const handleActivity = useCallback(() => {
    const now = Date.now()
    // Debounce: only reset if more than 1 second since last reset
    if (now - lastActivityCall.current > 1000) {
      lastActivityCall.current = now
      resetTimer()
    }
  }, [resetTimer])

  // Handle storage event for cross-tab synchronization
  const handleStorageChange = useCallback((event) => {
    if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
      // Another tab had activity, reset our timer
      const lastActivity = parseInt(event.newValue, 10)
      const timeSinceActivity = Date.now() - lastActivity
      
      if (timeSinceActivity < IDLE_TIMEOUT) {
        // Activity was recent, reset timer
        clearTimers()
        setIsWarning(false)
        
        const remainingIdle = IDLE_TIMEOUT - timeSinceActivity
        setRemainingTime(remainingIdle)

        if (remainingIdle > WARNING_BEFORE) {
          // Not in warning period yet
          warningTimeoutRef.current = setTimeout(() => {
            handleWarning()
          }, remainingIdle - WARNING_BEFORE)
        }

        timeoutRef.current = setTimeout(() => {
          handleTimeout()
        }, remainingIdle)
      }
    }
  }, [clearTimers, handleWarning, handleTimeout])

  // Set up event listeners and timers
  useEffect(() => {
    if (!enabled) {
      clearTimers()
      return
    }

    // Initialize timer
    resetTimer()

    // Add activity event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    // Add storage event listener for cross-tab sync
    window.addEventListener("storage", handleStorageChange)

    // Cleanup
    return () => {
      clearTimers()
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [enabled, resetTimer, handleActivity, handleStorageChange, clearTimers])

  return {
    resetTimer,
    isWarning,
    remainingTime,
  }
}

export default useIdleTimeout
